/**
 * STORY-STRIPE-008 + STORY-STRIPE-011 — HQ Admin Stripe Console
 *
 * Three tabs:
 *   1. Products & Prices — create new subscription tiers live in Stripe
 *   2. Coupons & Discounts — create coupons, share promo codes, apply to
 *      specific customers (dormant gym win-back flow)
 *   3. Revenue — local aggregate snapshot from D1 (active subs, past due,
 *      paused, commissions pending vs paid, last 30 days)
 *
 * Stripe is the source of truth — no local mirror tables. Products /
 * prices / coupons are fetched live on each tab view via TanStack Query.
 */
import { StatCard } from '@/components/custom/StatCard';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useApi } from '@/hooks/useApi';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Clock,
  CreditCard,
  DollarSign,
  Loader2,
  Package,
  Plus,
  Tag,
  TrendingUp,
  Users,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

// ---------- Shared types (match backend services/stripe-admin.ts) ----------

interface StripeProduct {
  id: string;
  name: string;
  description: string | null;
  active: boolean;
  default_price: string | null;
  created: number;
}

interface StripePrice {
  id: string;
  product: string;
  active: boolean;
  nickname: string | null;
  unit_amount: number | null;
  currency: string;
  recurring: { interval: 'day' | 'week' | 'month' | 'year' } | null;
  created: number;
}

interface StripeCoupon {
  id: string;
  name: string | null;
  percent_off: number | null;
  amount_off: number | null;
  currency: string | null;
  duration: 'forever' | 'once' | 'repeating';
  duration_in_months: number | null;
  max_redemptions: number | null;
  times_redeemed: number;
  redeem_by: number | null;
  valid: boolean;
  created: number;
}

interface StripePromotionCode {
  id: string;
  code: string;
  coupon: StripeCoupon | string;
  active: boolean;
  times_redeemed: number;
  expires_at: number | null;
}

interface StripeCustomer {
  id: string;
  email: string | null;
  name: string | null;
  discount: { coupon: StripeCoupon } | null;
}

interface RevenueSnapshot {
  subscribers: {
    individual_active: number;
    affiliate_active: number;
    individual_past_due: number;
    affiliate_past_due: number;
    individual_paused: number;
    affiliate_paused: number;
  };
  commissions: {
    pending_cents: number;
    paid_cents: number;
  };
  last_30_days: {
    new_individuals: number;
    new_affiliates: number;
  };
}

// ---------- Helpers ----------

function formatCents(cents: number | null, currency: string | null | undefined) {
  if (cents === null) return '—';
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: (currency ?? 'gbp').toUpperCase(),
  }).format(cents / 100);
}

function describeCoupon(coupon: StripeCoupon) {
  const amount =
    coupon.percent_off !== null
      ? `${coupon.percent_off}% off`
      : formatCents(coupon.amount_off, coupon.currency);
  const duration =
    coupon.duration === 'repeating' && coupon.duration_in_months
      ? `for ${coupon.duration_in_months} months`
      : coupon.duration;
  return `${amount} ${duration}`;
}

// ---------- Main component ----------

type Tab = 'products' | 'coupons' | 'revenue';

export default function AdminStripeConsole() {
  const [activeTab, setActiveTab] = useState<Tab>('products');

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gradient-gold">Stripe Console</h1>
          <p className="mt-1 text-muted-foreground">
            Manage prices, coupons, and revenue ops without leaving Ergshoot. Stripe is the source
            of truth — all changes go live immediately.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 rounded-md border border-border p-1 w-fit">
          {(
            [
              { key: 'products', label: 'Products & Prices', icon: Package },
              { key: 'coupons', label: 'Coupons & Discounts', icon: Tag },
              { key: 'revenue', label: 'Revenue', icon: TrendingUp },
            ] as const
          ).map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => setActiveTab(key)}
              className={`flex items-center gap-2 rounded px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === key
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted'
              }`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </div>

        {activeTab === 'products' && <ProductsTab />}
        {activeTab === 'coupons' && <CouponsTab />}
        {activeTab === 'revenue' && <RevenueTab />}
      </div>
    </DashboardLayout>
  );
}

// ---------- Products & Prices tab ----------

function ProductsTab() {
  const { api } = useApi();
  const queryClient = useQueryClient();
  const [isNewProductOpen, setIsNewProductOpen] = useState(false);

  const productsQuery = useQuery({
    queryKey: ['admin-stripe-products'],
    queryFn: async () => {
      const response = await api.get<{ products: StripeProduct[] }>('/admin/stripe/products');
      if (!response.success) throw new Error(response.error);
      return response.data?.products ?? [];
    },
  });

  const pricesQuery = useQuery({
    queryKey: ['admin-stripe-prices'],
    queryFn: async () => {
      const response = await api.get<{ prices: StripePrice[] }>('/admin/stripe/prices');
      if (!response.success) throw new Error(response.error);
      return response.data?.prices ?? [];
    },
  });

  const refreshAll = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-stripe-products'] });
    queryClient.invalidateQueries({ queryKey: ['admin-stripe-prices'] });
  };

  const pricesByProduct = (pricesQuery.data ?? []).reduce<Record<string, StripePrice[]>>(
    (acc, price) => {
      const bucket = acc[price.product] ?? [];
      bucket.push(price);
      acc[price.product] = bucket;
      return acc;
    },
    {}
  );

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle>Products & Prices</CardTitle>
            <CardDescription>
              Subscription tiers live in Stripe. Prices are immutable — to change an amount, create
              a new price and migrate subscribers.
            </CardDescription>
          </div>
          <Button onClick={() => setIsNewProductOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New Product
          </Button>
        </CardHeader>
        <CardContent>
          {productsQuery.isLoading || pricesQuery.isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : productsQuery.error ? (
            <p className="py-6 text-sm text-destructive">
              Failed to load products: {(productsQuery.error as Error).message}
            </p>
          ) : (productsQuery.data ?? []).length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              No active products in Stripe yet. Click "New Product" to create one.
            </p>
          ) : (
            <div className="space-y-3">
              {(productsQuery.data ?? []).map((product) => {
                const prices = pricesByProduct[product.id] ?? [];
                return (
                  <div key={product.id} className="rounded-md border border-border p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold">{product.name}</h3>
                          <Badge
                            variant="outline"
                            className="bg-green-500/10 text-green-500 border-green-500/20"
                          >
                            Active
                          </Badge>
                        </div>
                        {product.description && (
                          <p className="mt-1 text-sm text-muted-foreground">
                            {product.description}
                          </p>
                        )}
                        <p className="mt-1 text-xs text-muted-foreground font-mono">{product.id}</p>
                      </div>
                    </div>

                    {prices.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-border/50 space-y-1">
                        {prices.map((price) => (
                          <div key={price.id} className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">
                              {price.nickname ?? price.id}
                            </span>
                            <span className="font-medium">
                              {formatCents(price.unit_amount, price.currency)} /{' '}
                              {price.recurring?.interval ?? 'one-time'}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <NewProductDialog
        open={isNewProductOpen}
        onClose={() => setIsNewProductOpen(false)}
        onCreated={() => {
          refreshAll();
          setIsNewProductOpen(false);
        }}
      />
    </>
  );
}

function NewProductDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { api } = useApi();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [priceGbp, setPriceGbp] = useState('');
  const [interval, setInterval] = useState<'month' | 'year'>('month');

  const mutation = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = { name };
      if (description) body.description = description;
      if (priceGbp) {
        const amount = Number.parseFloat(priceGbp);
        if (Number.isFinite(amount) && amount > 0) {
          body.unit_amount = Math.round(amount * 100);
          body.currency = 'gbp';
          body.interval = interval;
        }
      }
      const response = await api.post<{ product: StripeProduct }>('/admin/stripe/products', body);
      if (!response.success) throw new Error(response.error);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Product created in Stripe');
      setName('');
      setDescription('');
      setPriceGbp('');
      onCreated();
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Failed to create product');
    },
  });

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New Stripe Product</DialogTitle>
          <DialogDescription>
            Creates a new subscription tier. Leave price blank to create the product without an
            attached price.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label htmlFor="prod-name">Name</Label>
            <Input
              id="prod-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Gym Pro"
            />
          </div>
          <div>
            <Label htmlFor="prod-desc">Description (optional)</Label>
            <Textarea
              id="prod-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Everything in Standard plus multi-gym support"
              rows={3}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="prod-price">Price (GBP, optional)</Label>
              <Input
                id="prod-price"
                type="number"
                step="0.01"
                min="0"
                value={priceGbp}
                onChange={(e) => setPriceGbp(e.target.value)}
                placeholder="99.00"
              />
            </div>
            <div>
              <Label htmlFor="prod-interval">Interval</Label>
              <Select
                id="prod-interval"
                value={interval}
                onChange={(e) => setInterval(e.target.value as 'month' | 'year')}
                options={[
                  { value: 'month', label: 'Monthly' },
                  { value: 'year', label: 'Yearly' },
                ]}
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => mutation.mutate()} disabled={!name.trim() || mutation.isPending}>
            {mutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Create Product
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Coupons tab ----------

function CouponsTab() {
  const { api } = useApi();
  const queryClient = useQueryClient();
  const [isNewCouponOpen, setIsNewCouponOpen] = useState(false);
  const [applyCoupon, setApplyCoupon] = useState<StripeCoupon | null>(null);

  const couponsQuery = useQuery({
    queryKey: ['admin-stripe-coupons'],
    queryFn: async () => {
      const response = await api.get<{
        coupons: StripeCoupon[];
        promotion_codes: StripePromotionCode[];
      }>('/admin/stripe/coupons');
      if (!response.success) throw new Error(response.error);
      return response.data;
    },
  });

  const pcByCoupon = (couponsQuery.data?.promotion_codes ?? []).reduce<
    Record<string, StripePromotionCode[]>
  >((acc, pc) => {
    const couponId = typeof pc.coupon === 'string' ? pc.coupon : pc.coupon.id;
    if (!acc[couponId]) acc[couponId] = [];
    acc[couponId].push(pc);
    return acc;
  }, {});

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle>Coupons & Discounts</CardTitle>
            <CardDescription>
              Create coupons with optional promotion codes, or apply a coupon directly to a specific
              customer (dormant-gym win-back).
            </CardDescription>
          </div>
          <Button onClick={() => setIsNewCouponOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New Coupon
          </Button>
        </CardHeader>
        <CardContent>
          {couponsQuery.isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : couponsQuery.error ? (
            <p className="py-6 text-sm text-destructive">
              Failed to load coupons: {(couponsQuery.error as Error).message}
            </p>
          ) : (couponsQuery.data?.coupons ?? []).length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              No coupons in Stripe yet. Click "New Coupon" to create one.
            </p>
          ) : (
            <div className="space-y-3">
              {(couponsQuery.data?.coupons ?? []).map((coupon) => {
                const pcs = pcByCoupon[coupon.id] ?? [];
                return (
                  <div key={coupon.id} className="rounded-md border border-border p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold">{coupon.name ?? coupon.id}</h3>
                          {coupon.valid ? (
                            <Badge
                              variant="outline"
                              className="bg-green-500/10 text-green-500 border-green-500/20"
                            >
                              Valid
                            </Badge>
                          ) : (
                            <Badge
                              variant="outline"
                              className="bg-gray-500/10 text-gray-500 border-gray-500/20"
                            >
                              Expired
                            </Badge>
                          )}
                        </div>
                        <p className="mt-1 text-sm text-muted-foreground">
                          {describeCoupon(coupon)}
                        </p>
                        <p className="mt-1 text-xs text-muted-foreground">
                          Redeemed {coupon.times_redeemed}
                          {coupon.max_redemptions ? ` / ${coupon.max_redemptions}` : ''}
                        </p>
                        {pcs.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {pcs.map((pc) => (
                              <code
                                key={pc.id}
                                className="rounded bg-muted px-2 py-0.5 text-xs font-mono"
                              >
                                {pc.code}
                              </code>
                            ))}
                          </div>
                        )}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setApplyCoupon(coupon)}
                        disabled={!coupon.valid}
                      >
                        Apply to customer
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <NewCouponDialog
        open={isNewCouponOpen}
        onClose={() => setIsNewCouponOpen(false)}
        onCreated={() => {
          queryClient.invalidateQueries({ queryKey: ['admin-stripe-coupons'] });
          setIsNewCouponOpen(false);
        }}
      />

      {applyCoupon && (
        <ApplyCouponDialog coupon={applyCoupon} onClose={() => setApplyCoupon(null)} />
      )}
    </>
  );
}

function NewCouponDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { api } = useApi();
  const [name, setName] = useState('');
  const [discountType, setDiscountType] = useState<'percent' | 'amount'>('percent');
  const [percentOff, setPercentOff] = useState('');
  const [amountOffGbp, setAmountOffGbp] = useState('');
  const [duration, setDuration] = useState<'forever' | 'once' | 'repeating'>('once');
  const [durationMonths, setDurationMonths] = useState('');
  const [maxRedemptions, setMaxRedemptions] = useState('');
  const [promotionCode, setPromotionCode] = useState('');

  const mutation = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = { name, duration };
      if (discountType === 'percent') {
        const pct = Number.parseFloat(percentOff);
        if (!Number.isFinite(pct) || pct <= 0 || pct > 100) {
          throw new Error('Percent off must be between 1 and 100');
        }
        body.percent_off = pct;
      } else {
        const amt = Number.parseFloat(amountOffGbp);
        if (!Number.isFinite(amt) || amt <= 0) {
          throw new Error('Amount off must be greater than 0');
        }
        body.amount_off_cents = Math.round(amt * 100);
        body.currency = 'gbp';
      }
      if (duration === 'repeating') {
        const months = Number.parseInt(durationMonths, 10);
        if (!Number.isFinite(months) || months <= 0) {
          throw new Error('Duration in months is required for repeating coupons');
        }
        body.duration_in_months = months;
      }
      if (maxRedemptions) {
        const m = Number.parseInt(maxRedemptions, 10);
        if (Number.isFinite(m) && m > 0) body.max_redemptions = m;
      }
      if (promotionCode.trim()) body.promotion_code = promotionCode.trim();

      const response = await api.post('/admin/stripe/coupons', body);
      if (!response.success) throw new Error(response.error);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Coupon created in Stripe');
      setName('');
      setPercentOff('');
      setAmountOffGbp('');
      setDurationMonths('');
      setMaxRedemptions('');
      setPromotionCode('');
      onCreated();
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Failed to create coupon');
    },
  });

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>New Coupon</DialogTitle>
          <DialogDescription>
            Creates a coupon in Stripe. Add a promotion code to give customers a shareable string
            (e.g. FOUNDING50).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label htmlFor="coupon-name">Name</Label>
            <Input
              id="coupon-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Founding Member 50% Off"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="coupon-type">Discount type</Label>
              <Select
                id="coupon-type"
                value={discountType}
                onChange={(e) => setDiscountType(e.target.value as 'percent' | 'amount')}
                options={[
                  { value: 'percent', label: 'Percent off' },
                  { value: 'amount', label: 'Fixed amount off (GBP)' },
                ]}
              />
            </div>
            {discountType === 'percent' ? (
              <div>
                <Label htmlFor="coupon-percent">Percent off</Label>
                <Input
                  id="coupon-percent"
                  type="number"
                  min="1"
                  max="100"
                  value={percentOff}
                  onChange={(e) => setPercentOff(e.target.value)}
                  placeholder="50"
                />
              </div>
            ) : (
              <div>
                <Label htmlFor="coupon-amount">Amount off (GBP)</Label>
                <Input
                  id="coupon-amount"
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={amountOffGbp}
                  onChange={(e) => setAmountOffGbp(e.target.value)}
                  placeholder="10.00"
                />
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="coupon-duration">Duration</Label>
              <Select
                id="coupon-duration"
                value={duration}
                onChange={(e) => setDuration(e.target.value as 'forever' | 'once' | 'repeating')}
                options={[
                  { value: 'once', label: 'Once' },
                  { value: 'repeating', label: 'Repeating' },
                  { value: 'forever', label: 'Forever' },
                ]}
              />
            </div>
            {duration === 'repeating' && (
              <div>
                <Label htmlFor="coupon-months">Months</Label>
                <Input
                  id="coupon-months"
                  type="number"
                  min="1"
                  value={durationMonths}
                  onChange={(e) => setDurationMonths(e.target.value)}
                  placeholder="3"
                />
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="coupon-max">Max redemptions (optional)</Label>
              <Input
                id="coupon-max"
                type="number"
                min="1"
                value={maxRedemptions}
                onChange={(e) => setMaxRedemptions(e.target.value)}
                placeholder="100"
              />
            </div>
            <div>
              <Label htmlFor="coupon-code">Promotion code (optional)</Label>
              <Input
                id="coupon-code"
                value={promotionCode}
                onChange={(e) => setPromotionCode(e.target.value.toUpperCase())}
                placeholder="FOUNDING50"
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => mutation.mutate()} disabled={!name.trim() || mutation.isPending}>
            {mutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Create Coupon
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ApplyCouponDialog({
  coupon,
  onClose,
}: {
  coupon: StripeCoupon;
  onClose: () => void;
}) {
  const { api } = useApi();
  const [email, setEmail] = useState('');
  const [customers, setCustomers] = useState<StripeCustomer[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const search = async () => {
    if (!email.trim()) return;
    setIsSearching(true);
    try {
      const response = await api.get<{ customers: StripeCustomer[] }>(
        `/admin/stripe/customers?email=${encodeURIComponent(email.trim())}`
      );
      if (!response.success) throw new Error(response.error);
      setCustomers(response.data?.customers ?? []);
      if ((response.data?.customers ?? []).length === 0) {
        toast.info('No Stripe customers found for that email');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setIsSearching(false);
    }
  };

  const applyMutation = useMutation({
    mutationFn: async (customerId: string) => {
      const response = await api.post(`/admin/stripe/customers/${customerId}/discount`, {
        coupon_id: coupon.id,
      });
      if (!response.success) throw new Error(response.error);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Discount applied to customer');
      onClose();
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Failed to apply discount');
    },
  });

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Apply {coupon.name ?? coupon.id}</DialogTitle>
          <DialogDescription>
            Look up a Stripe customer by email, then apply <strong>{describeCoupon(coupon)}</strong>{' '}
            directly. This creates a one-off customer-specific discount on their next invoice.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label htmlFor="apply-email">Customer email</Label>
            <div className="flex gap-2">
              <Input
                id="apply-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') search();
                }}
                placeholder="owner@dormantgym.com"
              />
              <Button onClick={search} disabled={isSearching || !email.trim()}>
                {isSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Search'}
              </Button>
            </div>
          </div>

          {customers.length > 0 && (
            <div className="space-y-2">
              {customers.map((customer) => (
                <div
                  key={customer.id}
                  className="flex items-center justify-between rounded-md border border-border p-3"
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm">
                      {customer.name ?? customer.email ?? customer.id}
                    </p>
                    <p className="text-xs text-muted-foreground">{customer.email ?? '—'}</p>
                    <p className="text-xs text-muted-foreground font-mono">{customer.id}</p>
                    {customer.discount && (
                      <p className="text-xs text-orange-500 mt-1">
                        Already has discount: {customer.discount.coupon.name}
                      </p>
                    )}
                  </div>
                  <Button
                    size="sm"
                    onClick={() => applyMutation.mutate(customer.id)}
                    disabled={applyMutation.isPending}
                  >
                    Apply
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Revenue tab ----------

function RevenueTab() {
  const { api } = useApi();

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-stripe-revenue'],
    queryFn: async () => {
      const response = await api.get<RevenueSnapshot>('/admin/stripe/revenue');
      if (!response.success) throw new Error(response.error);
      return response.data;
    },
  });

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card>
        <CardContent className="py-6 text-sm text-destructive">
          Failed to load revenue snapshot:{' '}
          {error instanceof Error ? error.message : 'unknown error'}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Active subscribers</h2>
        <p className="text-sm text-muted-foreground">
          Sourced from D1 — updated live by Stripe webhooks.
        </p>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <StatCard
          title="Individuals — active"
          value={String(data.subscribers.individual_active)}
          icon={Users}
        />
        <StatCard
          title="Affiliates — active"
          value={String(data.subscribers.affiliate_active)}
          icon={Users}
        />
        <StatCard
          title="Past due (total)"
          value={String(data.subscribers.individual_past_due + data.subscribers.affiliate_past_due)}
          icon={CreditCard}
        />
        <StatCard
          title="Paused (total)"
          value={String(data.subscribers.individual_paused + data.subscribers.affiliate_paused)}
          icon={Clock}
        />
        <StatCard
          title="New subs last 30d"
          value={String(data.last_30_days.new_individuals + data.last_30_days.new_affiliates)}
          icon={TrendingUp}
        />
      </div>

      <div>
        <h2 className="text-lg font-semibold">Agent commissions</h2>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <StatCard
          title="Pending payouts"
          value={formatCents(data.commissions.pending_cents, 'gbp')}
          icon={DollarSign}
        />
        <StatCard
          title="Paid to date"
          value={formatCents(data.commissions.paid_cents, 'gbp')}
          icon={DollarSign}
        />
      </div>
    </div>
  );
}
